import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import EmptyState from "@/components/EmptyState";
import Table, { TableHeading, buildTableFooterLineItems } from "@/components/Table";
import SectionHeading from "@/components/SectionHeading";
import Button from "@/components/Button";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import ObjectHeading from "@/components/ObjectHeading";
import { renderStatus, renderPaymentType, renderPaymentStatus, paymentDate } from "../../_utils";
import { ORDER_QUERY, PDF_QUERY, COMMERCE_AUDIT_LOG_QUERY, SET_ORDER_DRAFT_MUTATION } from "./queries";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { OrderType, LineItemsType, OrderQuery, CommerceAuditLogQuery } from "./types";
import Progress from "@/components/Progress";
import BillingObjectDetailsSkeleton from "../_components/BillingObjectDetailsSkeleton";
import type { Path } from "@/router";
import { toFormat } from "@/lib/dateHelpers";
import type { Action } from "@/components/MenuButton";
import IconButton from "@/components/IconButton";
import Menu from "@/components/Menu";
import { useNavigate } from "@/router";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { OrderStage } from "../../types";
import { CancelOrderModal } from "./_components/CancelOrderModal";
import { MarkUncollectibleModal } from "./_components/MarkUncollectibleModal";
import { OpenOrderModal } from "./_components/OpenOrderModal";
import { ChangeDueDateModal } from "./_components/ChangeDueDateModal";
import { SetOrderDraftBlockedModal } from "./_components/SetOrderDraftBlockedModal";
import { openBrowser } from "@/lib/urlUtils";
import { getCopyPaymentUrlAction } from "../copyPaymentUrlAction";
import DetailsCard from "@/components/DetailsCard";
import PageTitle from "@/components/PageTitle";
import { sumBy } from "lodash";
import pluralize from "@/lib/pluralize";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useStepsList } from "../../_utils";
import { formattedAddressLines } from "@/lib/addressHelpers";
import HistorySection from "../_components/HistorySection";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import NotesTable, { useNoteDeepLink, usePinnedNotes } from "@/features/global/components/NotesTable";
import TasksTable from "@/features/global/components/TasksTable";
import AddTaskSideSheet from "@/features/global/components/AddTaskSideSheet/AddTaskSideSheet";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import Breadcrumb from "@/components/Breadcrumb";
import ListItem from "@/components/ListItem/ListItem";
import { useReturnTo } from "@/hooks/useSearchParams";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useFlags } from "@/hooks/useFeatureFlags";
import { NoteNotableValues } from "@/types";

interface LineItemTableData extends LineItemsType {
  removed?: boolean;
  price?: string | null;
  amount?: string | null;
  adjustment?: boolean;
  createdAt?: string;
  amountCents?: number;
  indent?: boolean;
}

const Order = () => {
  useRequireCapability({
    domain: DOMAIN.COMMERCE,
    permission: PERMISSION.viewOrders
  });
  const { orderId } = useParams();
  const { error, data, loading, refetch } = useQuery<OrderQuery>(ORDER_QUERY, {
    variables: { orderId }
  });
  const order = data?.commerceOrder;

  if (error) {
    return <EmptyState title="Error" caption="An error occurred while loading this order." iconName="error" />;
  } else if (order) {
    return <OrderWrapper order={order} refetch={refetch} />;
  } else if (loading) {
    return <BillingObjectDetailsSkeleton />;
  } else {
    return <EmptyState title="Order Not Found" caption="This order may have been removed." iconName="receipt_long" />;
  }
};

const OrderWrapper = ({ order, refetch }: { order: OrderType; refetch: () => void }) => {
  const { trackEvent } = useAmplitude();
  const {
    dueDate,
    balance,
    status,
    person,
    totalPaid,
    totalRefunded,
    description,
    pdfAvailable,
    eventRegistration
  }: OrderType = order;
  const { hasPermission } = useCapabilities();
  const flags = useFlags();
  const uncollectibleOrdersEnabled = !!flags["backoffice-uncollectible-orders"];
  const {
    highlightNoteId: deepLinkNoteId,
    openNoteId,
    getCopyLink,
    setOpenNoteInUrl
  } = useNoteDeepLink({
    dataReady: true
  });
  const returnTo = useReturnTo();
  const canManageTasks = useCanManageTasksBool();
  const showTasks = canManageTasks;
  const currentUser = useCurrentBackofficeUser();
  const canAddNotes = hasPermission(PERMISSION.manageOrders);
  const canAddTasks = showTasks;

  const {
    pinnedNotes,
    shouldAutoOpenNotes,
    highlightNoteId: pinnedHighlightNoteId
  } = usePinnedNotes({
    notes: order.notes || [],
    entityId: order.id,
    sessionStorageKey: "visitedOrderDetails"
  });
  const highlightNoteId = pinnedHighlightNoteId ?? deepLinkNoteId;

  const [openCancelOrderModal, setOpenCancelOrderModal] = useState(false);
  const [openMarkUncollectibleModal, setOpenMarkUncollectibleModal] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const navigate = useNavigate();
  const [openOpenOrderModal, setOpenOpenOrderModal] = useState(false);
  const [openChangeDueDateModal, setOpenChangeDueDateModal] = useState(false);
  const [openSetOrderDraftBlockedModal, setOpenSetOrderDraftBlockedModal] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [setOrderDraftMutation] = useMutation(SET_ORDER_DRAFT_MUTATION, {
    refetchQueries: [COMMERCE_AUDIT_LOG_QUERY]
  });

  const hasPaymentHistory = order.orderPayments.length > 0;
  const hasActiveLineItems = (order.lineItems || []).some((item) => !item.removedAt);

  const handleSetOrderDraft = useCallback(async () => {
    if (hasPaymentHistory) {
      setOpenSetOrderDraftBlockedModal(true);
      return;
    }

    const { data } = await setOrderDraftMutation({
      variables: { input: { orderId: order.id } }
    });

    if (data?.commerceSetOrderDraft?.success) {
      await refetch();
      showSnackbar("Order set to draft");
      trackEvent(
        BILLING_ANALYTICS_EVENTS.orderSetToDraft,
        getBillingAnalyticsData({
          source: "orders",
          orderId: order.id,
          action: "set_draft"
        })
      );
      return;
    }

    showErrorSnackbar(data?.commerceSetOrderDraft?.message || "Failed to set order to draft");
  }, [hasPaymentHistory, order.id, refetch, setOrderDraftMutation, trackEvent]);

  const [pdfQuery, { loading: pdfLoading }] = useLazyQuery<OrderQuery>(PDF_QUERY, {
    variables: { orderId: order.id }
  });

  const handlePDFClick = useCallback(async () => {
    const { data } = await pdfQuery();
    const pdfUrl = data?.commerceOrder?.pdfUrl;
    pdfUrl && openBrowser(pdfUrl);
  }, [pdfQuery]);

  const headerActions = useMemo<Action[]>(() => {
    const buttons: Action[] = [];

    if (status === OrderStage.DRAFT) {
      if (hasPermission(PERMISSION.manageOrders) && hasActiveLineItems) {
        buttons.push({
          text: "Open Order",
          variant: "filled",
          onClick: () => setOpenOpenOrderModal(true),
          testId: "open-order"
        });
      }
      if (hasPermission(PERMISSION.managePayments)) {
        buttons.push({
          text: "Edit Order",
          onClick: () => navigate("/billing/orders/edit-order/:orderId", { params: { orderId: order.id } }),
          section: "Manage"
        });
      }
    } else {
      if (hasPermission(PERMISSION.managePayments) && order.payable) {
        buttons.push({
          text: "Apply Payment",
          variant: "filled",
          onClick: () => navigate(`/billing/orders/apply-payment?orderId=${order.id}` as Path)
        });
      }
      if (
        hasPermission(PERMISSION.managePayments) &&
        status !== OrderStage.UNCOLLECTIBLE &&
        (totalPaid > 0 || (status === OrderStage.OPEN && totalPaid === 0))
      ) {
        const isEditableForEventRegistration = !eventRegistration || eventRegistration.event.activityType === "PAT";

        if (isEditableForEventRegistration) {
          buttons.push({
            text: "Edit Order",
            onClick: () => navigate("/billing/orders/edit-order/:orderId", { params: { orderId: order.id } }),
            section: "Manage"
          });
        }
      }
    }

    if (hasPermission(PERMISSION.manageOrders) && status === OrderStage.OPEN) {
      buttons.push({
        text: "Change Due Date",
        onClick: () => setOpenChangeDueDateModal(true),
        section: "Manage"
      });
    }

    const copyPaymentUrlAction = getCopyPaymentUrlAction(
      { id: order.id, type: order.type, payable: order.payable },
      { section: "Sharing", menuOnly: true }
    );
    if (copyPaymentUrlAction) {
      buttons.push(copyPaymentUrlAction);
    }

    if (canAddNotes) {
      buttons.push({
        text: "Add Note",
        variant: "outlined",
        onClick: () => setAddNoteOpen(true),
        testId: "add-note-action",
        section: "Actions"
      });
    }

    if (canAddTasks) {
      buttons.push({
        text: "Add Task",
        variant: "outlined",
        onClick: () => setAddTaskOpen(true),
        testId: "add-task-action",
        section: "Actions"
      });
    }

    if (
      hasPermission(PERMISSION.manageOrders) &&
      (status === OrderStage.DRAFT || status === OrderStage.OPEN) &&
      totalPaid === 0
    ) {
      buttons.push({
        text: "Cancel Order",
        onClick: () => setOpenCancelOrderModal(true),
        testId: "cancel-order",
        destructive: true
      });
    }

    if (
      uncollectibleOrdersEnabled &&
      hasPermission(PERMISSION.markOrdersUncollectible) &&
      status === OrderStage.OPEN &&
      totalPaid > 0 &&
      order.type === "dues"
    ) {
      buttons.push({
        text: "Mark Uncollectible",
        onClick: () => setOpenMarkUncollectibleModal(true),
        testId: "mark-uncollectible",
        destructive: true
      });
    }

    if (hasPermission(PERMISSION.manageOrders) && status === OrderStage.CANCELED) {
      buttons.push({
        text: "Set as Draft",
        menuLabel: (
          <>
            Mark as <strong>Draft</strong>
          </>
        ),
        variant: "filled",
        onClick: () => {
          void handleSetOrderDraft();
        },
        testId: "set-as-draft"
      });
    }

    return buttons;
  }, [
    status,
    hasPermission,
    uncollectibleOrdersEnabled,
    hasActiveLineItems,
    totalPaid,
    navigate,
    order.id,
    order.payable,
    order.type,
    handleSetOrderDraft,
    canAddNotes,
    canAddTasks,
    eventRegistration
  ]);

  const lineItems = (order?.lineItems || []).reduce<LineItemTableData[]>((acc, item) => {
    const { proration, utilization } = item;
    acc.push({
      ...item,
      price: centsToDollarsFormatted(item.priceCents),
      amount: centsToDollarsFormatted(item.priceCents * item.quantity),
      amountCents: item.priceCents * item.quantity
    });

    if (item.removedAt) {
      acc.push({
        ...item,
        subLineItem: true,
        removed: true,
        indent: true,
        amount: centsToDollarsFormatted(-1 * item.priceCents * item.quantity),
        amountCents: -1 * item.priceCents * item.quantity
      });
    }

    if (item.ledgerEntries.some((entry) => entry.type === "adjustment_partial_refund")) {
      acc.push({
        ...item,
        adjustment: true,
        indent: true,
        createdAt: item.ledgerEntries.find((entry) => entry.type === "adjustment_partial_refund")?.createdAt,
        amount: centsToDollarsFormatted(
          -(item.ledgerEntries.find((entry) => entry.type === "adjustment_partial_refund")?.amountCents ?? 0)
        ),
        amountCents: -(item.ledgerEntries.find((entry) => entry.type === "adjustment_partial_refund")?.amountCents ?? 0)
      });
    }

    if (proration?.proratedNumberOfMonths && proration.proratedPrice) {
      const adjustedPrice = proration.proratedPrice - proration.originalPrice;

      acc.push({
        ...item,
        id: `${item.id}-proration`,
        subLineItem: true,
        indent: true,
        name: `Proration ${proration.proratedNumberOfMonths} ${pluralize(
          item.proration.proratedNumberOfMonths,
          "Month"
        )} Original/Adjustment: ${centsToDollarsFormatted(proration.originalPrice)}/${centsToDollarsFormatted(
          adjustedPrice
        )}`
      });
    }

    if (utilization?.utilizedStart && utilization?.utilizedEnd) {
      const utilizedStart = toFormat(utilization?.utilizedStart, "MMM yyyy") || "";
      const utilizedEnd = toFormat(utilization?.utilizedEnd, "MMM yyyy") || "";

      acc.push({
        ...item,
        id: `${item.id}-utilization`,
        subLineItem: true,
        indent: true,
        name: `Dues (${utilizedStart} - ${utilizedEnd})  Paid/Credit: ${centsToDollarsFormatted(
          utilization.originalPrice
        )}/-${centsToDollarsFormatted(utilization.creditForwardedAmount)}`
      });
    }

    return acc;
  }, []);

  const lineItemsTotal = sumBy(lineItems, "amountCents");

  const { data: auditLogData, loading: auditLogDataLoading } = useQuery<CommerceAuditLogQuery>(
    COMMERCE_AUDIT_LOG_QUERY,
    {
      variables: { orderId: order.id },
      errorPolicy: "all"
    }
  );

  const auditLogEntries = auditLogData?.commerceAuditLog || [];

  const displayTitle = eventRegistration?.event?.title || description;
  const stepsList = useStepsList(auditLogEntries, 0);

  return (
    <>
      <PageTitle
        pattern={location.pathname}
        title={`Billing | Orders | Order Details | ${displayTitle} for #${person.pgaId}`}
      />
      <div data-testid="order-details-page">
        <Breadcrumb
          title="Orders"
          backLink={{ to: "/billing/orders" }}
          compactTitle="Order Details"
          onBackClick={() => {
            const backTo = returnTo || "/billing/orders";
            navigate((backTo as Path) || ("/billing/orders" as Path));
          }}
        />
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <ObjectHeading
            title={`Order: ${displayTitle} for #${person.pgaId}`}
            iconName="receipt_long"
            isDetailsView
            tag={<div>{renderStatus(status, order.paymentStatus, "strong")}</div>}
            metaData={[
              ...(dueDate ? [{ text: toFormat(dueDate) || "", label: "Due Date" }] : []),
              ...(lineItemsTotal
                ? [
                    {
                      text: centsToDollarsFormatted(lineItemsTotal) || "",
                      label: "Amount"
                    }
                  ]
                : []),
              ...(balance
                ? [
                    {
                      text: centsToDollarsFormatted(balance) || "",
                      label: "Balance"
                    }
                  ]
                : [])
            ]}
            actions={headerActions}
            pinnedNotes={pinnedNotes}
            initialNotesDialogOpen={shouldAutoOpenNotes}
          />
        </div>
        {description && (
          <>
            <SectionHeading heading="Description" />
            <ListItem iconName="description" iconBackground compact headline={description} />
          </>
        )}
        {pdfAvailable && (
          <div className={description ? "mt-4" : "mt-6"}>
            <Button onClick={handlePDFClick} variant="outlined" testId="order-pdf">
              <div className="flex items-center justify-center">
                {pdfLoading && (
                  <div className="mr-2">
                    <Progress size={24} />
                  </div>
                )}
                View Order PDF
              </div>
            </Button>
          </div>
        )}
        <HistorySection stepsList={stepsList} loading={auditLogDataLoading} />
        <SectionHeading heading="Summary" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          {person && (
            <div>
              <DetailsCard
                heading="Billed To"
                options={[
                  {
                    picture: person.profilePhoto,
                    iconName: "person",
                    overline: "Name",
                    headline: `${person.firstName} ${person.lastName}`,
                    supportingText: "",
                    action: () => setIsDetailsPanelOpen(true),
                    actionIcon: "open_in_full"
                  }
                ]}
              />
            </div>
          )}
          {person.contactAddress &&
            (() => {
              const addressLines = formattedAddressLines(person.contactAddress);
              return (
                <DetailsCard
                  heading="Billing Address"
                  options={[
                    {
                      iconName: "home",
                      overline: "Home",
                      headline: (
                        <div>
                          <div>{addressLines.line1}</div>
                          <div>{addressLines.line2}</div>
                        </div>
                      )
                    }
                  ]}
                />
              );
            })()}
          {person.email && (
            <DetailsCard
              heading="Email Address"
              options={[
                {
                  iconName: "mail",
                  headline: person.email
                }
              ]}
            />
          )}
          {eventRegistration && (
            <DetailsCard
              heading="Event"
              options={[
                {
                  iconName: "calendar_month",
                  overline: "Event",
                  headline: eventRegistration.event.title,
                  supportingText: toFormat(eventRegistration.event.startsAt, "MMM d, yyyy") || "",
                  action: () =>
                    navigate(
                      `/events/${eventRegistration.event.slug}?return_to=${encodeURIComponent(
                        location.pathname
                      )}` as Path
                    )
                }
              ]}
            />
          )}
        </div>
        <TableHeading heading="Products" />
        <Table
          data={lineItems}
          loading={false}
          renderNoDataEmptyState={() => (
            <EmptyState title="No Products" caption="This order does not have any products." iconName="box" />
          )}
          columns={[
            {
              header: "Product Name",
              accessorKey: "name",
              enableSorting: false,
              size: 600,
              meta: {
                className: getStickyColumnClasses(!!lineItems?.length),
                headerClassName: "hide-header-in-column"
              },
              cell: ({ row }) => {
                if (row.original.removed) {
                  return <div>Removed from order</div>;
                }
                if (row.original.adjustment) {
                  return <div>Partial Refund Adjustment</div>;
                }
                if (row.original.subLineItem) {
                  return <div>{row.original.name}</div>;
                }
                return <div>{row.original.name}</div>;
              }
            },
            {
              header: "Price",
              accessorKey: "price",
              size: 400,
              enableSorting: false,
              meta: { className: "text-right", headerClassName: "justify-end" },
              cell: ({ row }) => <div>{row.original.subLineItem ? "" : row.original.price}</div>
            },
            {
              header: "Quantity",
              accessorKey: "quantity",
              size: 400,
              enableSorting: false,
              meta: { className: "text-right", headerClassName: "justify-end" },
              cell: ({ row }) => <div>{row.original.subLineItem ? "" : row.original.quantity}</div>
            },
            {
              header: "Amount",
              accessorKey: "amount",
              enableSorting: false,
              size: 40,
              meta: { className: "text-right", headerClassName: "justify-end" }
            }
          ]}
          renderFooter={() => (
            <>
              {buildTableFooterLineItems({
                items: [
                  {
                    label: "Order Total",
                    value: lineItemsTotal,
                    isEmphasized: true
                  }
                ],
                isSingle: true
              })}
              {buildTableFooterLineItems({
                items: [
                  {
                    label: "Total Amount Paid",
                    value: -(totalPaid + totalRefunded)
                  },
                  ...(totalRefunded > 0 ? [{ label: "Total Refund Amount", value: totalRefunded }] : []),
                  { label: "Balance Due", value: balance, isEmphasized: true }
                ],
                showTopBorder: true
              })}
            </>
          )}
        />
        {order.orderPayments.length > 0 && hasPermission(PERMISSION.viewPayments) && <Payments order={order} />}

        <NotesTable
          data={order.notes || []}
          hideControls
          tableHeading="Notes"
          tableBody="View and manage notes for this order"
          highlightNoteId={highlightNoteId}
          openNoteId={openNoteId}
          onOpenNoteChange={setOpenNoteInUrl}
          getCopyLink={getCopyLink}
          noteEditing={{
            enabled: hasPermission(PERMISSION.manageOrders),
            notableId: order.id,
            notableType: NoteNotableValues.ORDER,
            refetchQueries: [ORDER_QUERY],
            pgaId: person.pgaId
          }}
        />

        {showTasks && (
          <TasksTable
            referenceableId={order.id}
            referenceableType="ORDER"
            tableHeading="Tasks"
            tableBody="View and manage tasks for this order"
            hideControls
          />
        )}

        {openCancelOrderModal && (
          <CancelOrderModal
            onDismiss={() => {
              setOpenCancelOrderModal(false);
            }}
            order={order}
            refetch={refetch}
          />
        )}
        {openMarkUncollectibleModal && (
          <MarkUncollectibleModal
            onDismiss={() => {
              setOpenMarkUncollectibleModal(false);
            }}
            order={order}
            refetch={refetch}
          />
        )}
        {openOpenOrderModal && (
          <OpenOrderModal
            onDismiss={() => {
              setOpenOpenOrderModal(false);
            }}
            order={order}
            refetch={refetch}
          />
        )}
        {openChangeDueDateModal && (
          <ChangeDueDateModal
            onDismiss={() => {
              setOpenChangeDueDateModal(false);
            }}
            order={order}
            refetch={refetch}
          />
        )}
        {openSetOrderDraftBlockedModal && (
          <SetOrderDraftBlockedModal
            onDismiss={() => {
              setOpenSetOrderDraftBlockedModal(false);
            }}
          />
        )}
      </div>
      <PersonSideSheet
        open={isDetailsPanelOpen}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={person.pgaId}
        showEmployment={true}
        showFacility={true}
      />
      {canAddNotes && (
        <AddEditNoteSideSheet
          open={addNoteOpen}
          onClose={() => setAddNoteOpen(false)}
          notableId={order.id}
          notableType={NoteNotableValues.ORDER}
          refetchQueries={[ORDER_QUERY]}
          currentUserId={currentUser?.id}
          pgaId={person.pgaId}
        />
      )}
      {canAddTasks && (
        <AddTaskSideSheet
          open={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          currentUser={currentUser}
          defaultReferences={[{ referenceableId: order.id, referenceableType: "ORDER" }]}
          refetchQueries={[TASKS_QUERY]}
        />
      )}
    </>
  );
};

const Payments = ({ order }: { order: OrderType }) => {
  const { orderPayments } = order;
  const navigate = useNavigate();

  const copyCopNumber = async (copNumber: string) => {
    if (!navigator?.clipboard?.writeText) {
      showErrorSnackbar("Failed to copy COP number to clipboard");
      return;
    }

    try {
      await navigator.clipboard.writeText(copNumber);
      showSnackbar("COP number copied to clipboard", { close: true });
    } catch {
      showErrorSnackbar("Failed to copy COP number to clipboard");
    }
  };

  return (
    <>
      <SectionHeading heading="Payments" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        {orderPayments.map((orderPayment) => {
          return (
            <div key={orderPayment.id}>
              <DetailsCard
                className="py-4 pl-4 pr-0"
                options={[
                  {
                    iconName: "monetization_on",
                    overline: `${renderPaymentType(orderPayment.payment.type)}`,
                    headline: `${orderPayment.payment.payer.name}`,
                    supportingText: `${paymentDate(orderPayment.payment.type, orderPayment.payment.paidAt)}`,
                    action: () =>
                      navigate(`/billing/orders/:orderId/payment/:paymentId` as Path, {
                        params: {
                          orderId: order.id,
                          paymentId: orderPayment.payment.id
                        }
                      }),
                    renderActions: () => (
                      <div className="flex items-center -mt-3">
                        <span className="text-label-small text-sys-brand-on-surface-variant">
                          {centsToDollarsFormatted(orderPayment.amountCents)}
                        </span>
                        <Menu
                          AnchorComponent={
                            <IconButton
                              name="more_vert"
                              className="text-sys-brand-on-surface"
                              iconClassName="font-extrabold"
                              data-testid={`payment-card-actions-${orderPayment.id}`}
                            />
                          }
                          menuItems={[
                            {
                              label: "View Payment",
                              onClick: () =>
                                navigate(`/billing/orders/:orderId/payment/:paymentId` as Path, {
                                  params: {
                                    orderId: order.id,
                                    paymentId: orderPayment.payment.id
                                  }
                                })
                            },
                            {
                              label: "Copy COP Number",
                              copyFieldLabel: "COP number",
                              copyFieldId: orderPayment.id,
                              onClick: () => {
                                void copyCopNumber(orderPayment.id);
                              },
                              dataTestId: `copy-cop-number-${orderPayment.id}`
                            }
                          ]}
                        />
                      </div>
                    ),
                    status: () => renderPaymentStatus(orderPayment.payment.status, "strong")
                  }
                ]}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Order;
